/**
 * B76C · screen 2b — THE ATHLETE MEMBERSHIP SHEET.
 *
 * The one paid thing in the product, presented once, from four entry points
 * (1a footer row, 1b chat boundary, 1c plan-entry boundary, 4b Settings role
 * card). Bottom sheet on mobile, modal on desktop — the `CreateTeamModal`
 * variant pattern, because there is no shared sheet primitive in this codebase
 * and every surface owns its own chrome.
 *
 * ── WHAT THIS COMPONENT IS, AND IS NOT ────────────────────────────────────
 * It is a WRAPPER: the ratified title + body, then whichever purchase rail the
 * platform demands. It owns no purchase logic on native and no product
 * catalogue on either rail.
 *
 *   NATIVE → `<NativeSubscriptionPanel>` (REL-24), mounted as built and
 *            READ-ONLY to this lane. Its StoreKit `displayPrice` (localized,
 *            never our string), its verbatim `RENEWAL_DISCLOSURE`, its
 *            always-rendered "Restore purchases", and its Privacy Policy ·
 *            Terms of Use links are exactly what App Review looks for, and
 *            they arrive here untouched. The design's two product rows ARE
 *            that panel's product rows: StoreKit is the catalogue.
 *
 *   WEB    → the `SubscriptionSection` precedent: allowlisted priceIds from
 *            `GET /api/pricing`, `POST /api/checkout`, redirect to Stripe.
 *            Two rows, the SAME two athlete products. No new SKUs.
 *
 * ⚖️ NO PRICE STRING OF OUR OWN, on either rail. Native reads StoreKit; web
 * reads the server's published amounts. The design annotates $12.99 / $129.99
 * because that is what those sources say today, not because the sheet should
 * assert it — a hardcoded price is how a repricing ships a lie.
 *
 * ⚖️ THE MINOR GATE RIDES ALONG. `NativeSubscriptionPanel` already refuses
 * purchase controls under 18 (founder ruling 2026-08-19) and the web rail
 * already refuses checkout for a derived minor; this wrapper adds no bypass
 * and takes none away. A coach account IS an adult account by construction
 * (3d), so the case is rare here, but the gate is not this sheet's to relax.
 *
 * 🏷️ The title / body / row labels below are the ratified 2b strings from the
 * CD handoff. House register: no dashes, no exclamations, never "safe".
 */

import { useEffect, useRef, useState } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useServerFlag } from '../../hooks/useServerFlag';
import { useUserTier } from '../../hooks/useUserTier';
import { apiUrl } from '../../lib/apiBase';
import { isNativeApp } from '../../lib/platform';
import { NativeSubscriptionPanel } from '../settings/sections/NativeSubscriptionPanel';
import styles from './AthleteMembershipSheet.module.css';

/** Ratified 2b copy (CD handoff, screen 02b-membership-sheet). */
export const MEMBERSHIP_SHEET_COPY = {
  title: 'Athlete membership',
  body: 'Unlimited coach chat, a personalised plan and race telemetry. Your coaching side stays free either way.',
  monthlyName: 'Monthly',
  monthlyPeriod: 'per month',
  annualName: 'Annual',
  annualPeriod: 'per year',
  cta: 'Subscribe',
  close: 'Close',
  loading: 'Loading your plan…',
  loadError: "Couldn't load your plan. Reload the page or contact support.",
  // B377 — what a known minor reads where the disabled Subscribe button sits.
  // Provisional until the founder ratifies the lane report's string list.
  minorLine: 'Upgrades for athletes your age are set up by a parent or guardian.',
  minorLink: 'Open Settings',
} as const;

/** The `/api/pricing` shape, as `SubscriptionSection` consumes it. */
interface PublishedPrice {
  monthly?: string;
  annual?: string;
  monthlyAmount?: number;
  annualAmount?: number;
}

type WebRow = {
  key: 'monthly' | 'annual';
  name: string;
  period: string;
  priceId: string | undefined;
  amount: number | undefined;
};

export interface AthleteMembershipSheetProps {
  variant: 'modal' | 'sheet';
  onClose: () => void;
}

export function AthleteMembershipSheet({ variant, onClose }: AthleteMembershipSheetProps) {
  const isSheet = variant === 'sheet';
  const dialogRef = useRef<HTMLDivElement>(null);
  const native = isNativeApp();
  const { data, loading, error } = useUserTier();
  // REL-24 — `storekit_v1` gates the PURCHASE CONTROLS only, exactly as the
  // settings surface passes it. The panel renders either way.
  const storeKitOn = useServerFlag('storekit_v1');

  // ESC dismisses. No focus trap of our own: the native rail's controls and
  // the web rows are the only focusables, and the panel is a leaf.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={isSheet ? styles.sheetBackdrop : styles.modalBackdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={isSheet ? styles.sheetContainer : styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="athlete-membership-title"
      >
        {isSheet && <div className={styles.handle} aria-hidden="true" />}
        <h2 id="athlete-membership-title" className={styles.title}>
          {MEMBERSHIP_SHEET_COPY.title}
        </h2>
        <p className={styles.body}>{MEMBERSHIP_SHEET_COPY.body}</p>

        {loading && (
          <p className={styles.loading} role="status">
            {MEMBERSHIP_SHEET_COPY.loading}
          </p>
        )}
        {error && !loading && (
          <p className={styles.error} role="alert">
            {MEMBERSHIP_SHEET_COPY.loadError}
          </p>
        )}

        {data && !loading && (
          <div className={native ? styles.nativeRail : styles.webRail} data-rail={native ? 'native' : 'web'}>
            {native ? (
              <NativeSubscriptionPanel data={data} purchaseEnabled={storeKitOn} />
            ) : (
              <WebCheckoutRail derivedMinor={data.derivedMinor === true} />
            )}
          </div>
        )}

        <button type="button" className={styles.closeBtn} onClick={onClose}>
          {MEMBERSHIP_SHEET_COPY.close}
        </button>
      </div>
    </div>
  );
}

/**
 * The WEB rail — the `SubscriptionSection` checkout precedent, narrowed to the
 * two athlete products and dressed as the 2b product rows.
 *
 * A missing price id hides nothing and enables nothing: the row still names
 * the product (so the sheet reads the same everywhere), but it cannot be
 * selected, because a CTA that opens a dead checkout is worse than a row that
 * plainly is not for sale right now. That is the shipped rule on previews,
 * where there are no Stripe env vars at all.
 */
function WebCheckoutRail({ derivedMinor }: { derivedMinor: boolean }) {
  const fetchAuthed = useAuthFetch();
  // B377 — with `guardian_license_v1` on, the minor line also points at the
  // Settings page where a parent or guardian's route is described.
  const guardianLicenseOn = useServerFlag('guardian_license_v1');
  const [prices, setPrices] = useState<Record<string, PublishedPrice> | null>(null);
  const [selected, setSelected] = useState<'monthly' | 'annual'>('monthly');
  const [busy, setBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    // A known minor never opens checkout, so never fetch prices for one
    // (the SubscriptionSection rule, verbatim).
    if (derivedMinor) return;
    let alive = true;
    fetch(apiUrl('/api/pricing'))
      .then((r) => r.json())
      .then((j: { prices?: Record<string, PublishedPrice> }) => {
        if (alive) setPrices(j?.prices ?? null);
      })
      .catch(() => {
        /* pricing unreachable → the rows render unpurchasable */
      });
    return () => {
      alive = false;
    };
  }, [derivedMinor]);

  const athlete = prices?.athlete;
  const rows: WebRow[] = [
    {
      key: 'monthly',
      name: MEMBERSHIP_SHEET_COPY.monthlyName,
      period: MEMBERSHIP_SHEET_COPY.monthlyPeriod,
      priceId: athlete?.monthly,
      amount: athlete?.monthlyAmount,
    },
    {
      key: 'annual',
      name: MEMBERSHIP_SHEET_COPY.annualName,
      period: MEMBERSHIP_SHEET_COPY.annualPeriod,
      priceId: athlete?.annual,
      amount: athlete?.annualAmount,
    },
  ];

  const selectedRow = rows.find((r) => r.key === selected);

  const startCheckout = async () => {
    const priceId = selectedRow?.priceId;
    if (!priceId || busy) return;
    setBusy(true);
    setCheckoutError(null);
    try {
      const res = await fetchAuthed('/api/checkout', {
        method: 'POST',
        body: JSON.stringify({ priceId }),
      });
      const json = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!res.ok || !json.url) throw new Error(json.error || 'Could not start checkout');
      window.location.assign(json.url);
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <>
      {/* B377 — a known minor cannot buy here. Say who can, above the (still
          disabled) rows and Subscribe button, in the sheet's own body style.
          The link is plain text-decoration inline: this lane does not own the
          sheet's stylesheet. */}
      {derivedMinor && (
        <p className={styles.body}>
          {MEMBERSHIP_SHEET_COPY.minorLine}
          {guardianLicenseOn && (
            <>
              {' '}
              <a
                href="/v2/settings#subscription"
                style={{ textDecoration: 'underline', fontWeight: 600 }}
              >
                {MEMBERSHIP_SHEET_COPY.minorLink}
              </a>
            </>
          )}
        </p>
      )}
      <div className={styles.rows} role="radiogroup" aria-label={MEMBERSHIP_SHEET_COPY.title}>
        {rows.map((row) => (
          <button
            key={row.key}
            type="button"
            role="radio"
            aria-checked={selected === row.key}
            className={styles.productRow}
            data-selected={selected === row.key ? 'true' : undefined}
            disabled={!row.priceId || busy}
            onClick={() => setSelected(row.key)}
          >
            <span className={styles.productLeft}>
              <span className={styles.productName}>{row.name}</span>
              <span className={styles.productPeriod}>{row.period}</span>
            </span>
            {typeof row.amount === 'number' && (
              <span className={styles.productPrice}>${row.amount}</span>
            )}
          </button>
        ))}
      </div>

      <button
        type="button"
        className={styles.cta}
        disabled={!selectedRow?.priceId || busy || derivedMinor}
        onClick={() => void startCheckout()}
      >
        {busy ? 'Opening…' : MEMBERSHIP_SHEET_COPY.cta}
      </button>

      {checkoutError && (
        <p className={styles.error} role="alert">
          {checkoutError}
        </p>
      )}
    </>
  );
}
